import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isAdmin, isEditor, toast, useStore } from '../store/store';
import { queueRecordEdit, saveSheetValue } from './actions';
import { setData, setSheetView, useData } from './dataStore';
import { CategoryFilters } from './CategoryFilters';
import { CATEGORY_FILTERS, NO_FILTERS, passesFilters } from './filters';
import { useRecordsByGuid } from './hooks';
import { DataIcon } from './DataIcon';
import { NewRecordDialog } from './NewRecordDialog';
import { ViewSwitch } from './ViewSwitch';
import { categoryIcon } from './categories';
import {
  enumValues,
  fieldHelp,
  fieldLabel,
  formatNumber,
  humanize,
  pickerFor,
  settingKey,
  visibilityOf,
} from './schema';
import {
  DEFAULT_VIEW,
  SHEETS,
  cellChange,
  cellText,
  cellValue,
  filterRows,
  holdsValue,
  isFilterActive,
  parseInput,
  parseTsv,
  perEntry,
  sheetColumns,
  sheetRows,
  sortRows,
  statusText,
  toTsv,
  valueOptions,
  valueText,
  withCellValue,
  type ColumnFilter,
  type EditorKind,
  type SheetColumn,
  type SheetCtx,
  type SheetRow,
  type SheetView,
  type ValueOption,
} from './sheet';
import { same } from './values';
import type { DataClassRecord, DataFieldRecord } from './types';

// The sheet view (kalkylark): a category as a spreadsheet, one row per quest step or dialogue page.
// Select cells with the mouse or the keyboard, edit in place, filter every column, copy and paste with
// Excel / Google Sheets. Edits go through the same save as the form (validated, become proposals);
// our own columns (difficulty, notes) are saved apart and never reach Unity. Helpers: sheet.ts.

const HIDDEN_KEY = 'mapeditor.sheet.hidden.';
const WIDTHS_KEY = 'mapeditor.sheet.widths.';
const FULL_TEXT_KEY = 'mapeditor.sheet.fullText';
const NO_OWN: Record<string, string> = {};
const MAX_PASTE = 500;

function loadPref<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function savePref(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode etc. — a per-viewer convenience only */
  }
}

// Kept while a record is open, so "back" lands where you were.
const scrollMemory = new Map<string, { top: number; left: number }>();
const selMemory = new Map<string, Sel | null>();

interface Choice {
  value: string;
  label: string;
}

interface Col extends SheetColumn {
  label: string;
  tooltip: string;
  /** How the cell is edited; null = read-only column. */
  editor: EditorKind | null;
  choices?: Choice[];
  vocab?: string[];
  int: boolean;
  numeric: boolean;
}

/** The active cell (row, col) and the other corner of the selection (endRow, endCol), by id. */
interface Sel {
  row: string;
  col: string;
  endRow: string;
  endCol: string;
}

interface Editing {
  row: string;
  col: string;
  /** The key that started the edit (typing replaces the cell, as in Excel). */
  initial?: string;
}

interface Menu {
  col: string;
  x: number;
  y: number;
}

type Move = 'down' | 'up' | 'right' | 'left' | null;
type DoneFn = (row: SheetRow, col: Col, value: unknown, move: Move, refocus: boolean) => void;

interface Perm {
  /** may write our own columns */
  editor: boolean;
  /** may change record values in this category */
  values: boolean;
  admin: boolean;
  keyField: string;
}

function toColumn(
  c: SheetColumn,
  classes: Record<string, DataClassRecord>,
  settings: Record<string, DataFieldRecord>,
  vocabularies: Record<string, string[]> | null | undefined,
  lang: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): Col | null {
  const f = c.field;
  const setting = f ? settings[settingKey(f.owner, f.name)] : undefined;
  const visibility = f ? visibilityOf(f, settings) : 'edit';
  if (visibility === 'hidden' && c.cell.kind !== 'name') return null;
  const col: Col = {
    ...c,
    label:
      c.cell.kind === 'name'
        ? t('common.name')
        : f
          ? fieldLabel(f, setting, lang)
          : t(`data.sheet.col.${c.id}`),
    tooltip: f ? fieldHelp(f, setting, lang) : c.cell.kind === 'own' ? t('data.sheet.ownHint') : '',
    editor: null,
    int: f?.kind === 'int',
    numeric: f?.kind === 'int' || f?.kind === 'float' || c.cell.kind === 'index',
  };
  if (c.cell.kind === 'own') {
    const name = c.cell.name;
    col.editor = c.cell.options ? 'own' : c.cell.multiline ? 'multiline' : 'text';
    col.choices = c.cell.options?.map((v) => ({
      value: v,
      label: t(`data.sheet.options.${name}.${v}`, { defaultValue: v }),
    }));
  } else if (
    f &&
    visibility === 'edit' &&
    (c.cell.kind === 'name' || c.cell.kind === 'field' || c.cell.kind === 'entry')
  ) {
    const picker = pickerFor(f.name, setting);
    const vocab = vocabularies?.[f.name] ?? [];
    if (f.kind === 'string' && picker === 'vocab_strict' && vocab.length) {
      col.editor = 'enum';
      col.choices = vocab.map((v) => ({ value: v, label: v }));
    } else if (f.kind === 'string') {
      col.editor = f.multiline ? 'multiline' : 'text';
      if (picker === 'vocab') col.vocab = vocab;
    } else if (f.kind === 'int' || f.kind === 'float') col.editor = 'number';
    else if (f.kind === 'bool') col.editor = 'bool';
    else if (f.kind === 'enum') {
      col.editor = 'enum';
      col.choices = enumValues(classes, f.type).map((e) => ({ value: e.name, label: humanize(e.name) }));
    }
  }
  return col;
}

function cellEditable(row: SheetRow, col: Col, perm: Perm, ctx: SheetCtx): boolean {
  if (!col.editor) return false;
  if (col.cell.kind === 'own') return perm.editor;
  if (!perm.values || row.record.status === 'deprecated' || !holdsValue(row, col.cell, ctx)) return false;
  // Keys are game ids (Nakama saves keep them): locked once the asset is in Unity, as in the form.
  return !(
    col.cell.kind !== 'entry' &&
    col.field?.name === perm.keyField &&
    !!row.record.guid &&
    !perm.admin
  );
}

/** A record-level cell shows the same value on every row of the record, so its pending edit is per record. */
function pendingKey(row: SheetRow, col: Col): string {
  return `${perEntry(col.cell) ? row.id : row.record.id}|${col.id}`;
}

function currentValue(row: SheetRow, col: Col, ctx: SheetCtx, pending: Record<string, unknown>): unknown {
  const key = pendingKey(row, col);
  if (key in pending) return pending[key];
  return col.cell.kind === 'own'
    ? (ctx.own(row.record.id)[col.cell.name] ?? '')
    : cellValue(row, col.cell, ctx);
}

function displayText(row: SheetRow, col: Col, ctx: SheetCtx, pending: Record<string, unknown>): string {
  const key = pendingKey(row, col);
  if (!(key in pending)) return cellText(row, col, ctx);
  const v = pending[key];
  if (col.cell.kind === 'own') return col.choices?.find((c) => c.value === v)?.label ?? String(v ?? '');
  return valueText(v, col.field, ctx);
}

export function CategorySheet({ category }: { category: string }) {
  const { t, i18n } = useTranslation();
  const spec = SHEETS[category];
  const records = useData((s) => s.records);
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  const sheet = useData((s) => s.sheet);
  const vocabularies = useData((s) => s.lastImport?.vocabularies);
  const cat = useData((s) => s.categories.find((c) => c.key === category));
  const view = useData((s) => s.sheetViews[category]) ?? DEFAULT_VIEW;
  const selection = useData((s) => s.filters[category]) ?? NO_FILTERS;
  const user = useStore((s) => s.user);
  const users = useStore((s) => s.users);
  const byGuid = useRecordsByGuid();
  const [hidden, setHidden] = useState<string[]>(() => loadPref(HIDDEN_KEY + category, []));
  const [widths, setWidths] = useState<Record<string, number>>(() => loadPref(WIDTHS_KEY + category, {}));
  const [fullText, setFullText] = useState<boolean>(() => loadPref(FULL_TEXT_KEY, false));
  const [creating, setCreating] = useState(false);
  const [sel, setSel] = useState<Sel | null>(() => selMemory.get(category) ?? null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [pending, setPending] = useState<Record<string, unknown>>({});
  const [menu, setMenu] = useState<Menu | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const done = useRef<DoneFn | null>(null);

  const filterDefs = CATEGORY_FILTERS[category];
  const nameField = cat?.nameField ?? '';
  const lang = i18n.language;
  const perm = useMemo<Perm>(
    () => ({
      editor: isEditor(user),
      values: isEditor(user) && !!cat?.editable,
      admin: isAdmin(user),
      keyField: cat?.keyField ?? '',
    }),
    [user, cat?.editable, cat?.keyField],
  );

  const ctx = useMemo<SheetCtx>(
    () => ({
      list: spec.list,
      nameField,
      refName: (ref) => (ref.guid ? byGuid[ref.guid]?.name : ref.new ? records[ref.new]?.name : undefined),
      userName: (id) => users[id]?.name ?? '',
      own: (id) => sheet[id]?.values ?? NO_OWN,
      t: (key, opts) => t(key, opts),
    }),
    [spec.list, nameField, byGuid, records, users, sheet, t],
  );

  const inCategory = useMemo(
    () =>
      Object.values(records).filter(
        (r) => r.category === category && (!filterDefs || passesFilters(r, filterDefs, selection)),
      ),
    [records, category, filterDefs, selection],
  );
  const className = cat?.baseClass || inCategory[0]?.className || '';
  const allCols = useMemo(
    () =>
      sheetColumns(spec, classes, className, nameField)
        .map((c) => toColumn(c, classes, settings, vocabularies, lang, ctx.t))
        .filter((c): c is Col => c !== null),
    [spec, classes, className, nameField, settings, vocabularies, lang, ctx.t],
  );
  const shown = useMemo(
    () => allCols.filter((c) => c.id === 'name' || !hidden.includes(c.id)),
    [allCols, hidden],
  );
  const allRows = useMemo(() => sheetRows(inCategory, spec.list), [inCategory, spec.list]);
  const filtered = useMemo(() => filterRows(allRows, allCols, view, ctx), [allRows, allCols, view, ctx]);
  const rows = useMemo(
    () =>
      sortRows(
        filtered,
        allCols.find((c) => c.id === view.sort.col),
        view.sort.dir,
        ctx,
      ),
    [filtered, allCols, view.sort, ctx],
  );
  const recordCount = useMemo(() => new Set(rows.map((r) => r.record.id)).size, [rows]);
  const rowIdx = useMemo(() => new Map(rows.map((r, i) => [r.id, i])), [rows]);
  const colIdx = useMemo(() => new Map(shown.map((c, i) => [c.id, i])), [shown]);

  const pos = (rowId: string, colId: string) => {
    const r = rowIdx.get(rowId);
    const c = colIdx.get(colId);
    return r === undefined || c === undefined ? null : { r, c };
  };
  const at = sel ? pos(sel.row, sel.col) : null;
  const end = (sel && at && pos(sel.endRow, sel.endCol)) || at;
  const range =
    at && end
      ? {
          r0: Math.min(at.r, end.r),
          r1: Math.max(at.r, end.r),
          c0: Math.min(at.c, end.c),
          c1: Math.max(at.c, end.c),
        }
      : null;
  const multi = !!range && (range.r0 !== range.r1 || range.c0 !== range.c1);
  const widthOf = (id: string) => widths[id] ?? shown.find((c) => c.id === id)?.width ?? 140;

  useEffect(() => {
    selMemory.set(category, sel);
    if (!sel) return;
    const el = gridRef.current?.querySelector<HTMLElement>(
      `td[data-row="${CSS.escape(sel.endRow)}"][data-col="${CSS.escape(sel.endCol)}"]`,
    );
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [category, sel]);

  useLayoutEffect(() => {
    const el = gridRef.current;
    const saved = scrollMemory.get(category);
    if (el && saved) {
      el.scrollTop = saved.top;
      el.scrollLeft = saved.left;
    }
    return () => {
      if (el) scrollMemory.set(category, { top: el.scrollTop, left: el.scrollLeft });
    };
  }, [category]);

  useEffect(() => {
    const up = () => (dragging.current = false);
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  // ── view state ──
  const setView = (patch: Partial<SheetView>) => setSheetView(category, { ...view, ...patch });
  const setColumnFilter = (colId: string, f: ColumnFilter | undefined) => {
    const filters = { ...view.filters };
    if (f) filters[colId] = f;
    else delete filters[colId];
    setView({ filters });
  };
  const sortBy = (colId: string, dir?: 1 | -1) =>
    setView({
      sort: {
        col: colId,
        dir: dir ?? (view.sort.col === colId && view.sort.dir === 1 ? -1 : 1),
      },
    });
  const anyFilter =
    view.onlyChanged || !!view.search.trim() || Object.values(view.filters).some((f) => isFilterActive(f));
  const clearAll = () => setView({ filters: {}, search: '', onlyChanged: false });
  const toggleColumn = (id: string) => {
    const next = hidden.includes(id) ? hidden.filter((n) => n !== id) : [...hidden, id];
    setHidden(next);
    savePref(HIDDEN_KEY + category, next);
    if (!hidden.includes(id) && view.filters[id]) setColumnFilter(id, undefined);
  };
  const closeMenu = useCallback(() => setMenu(null), []);
  const toggleMenu = (colId: string, e: ReactMouseEvent<HTMLElement>) => {
    if (menu?.col === colId) return setMenu(null);
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({
      col: colId,
      x: Math.max(8, Math.min(rect.left - 200, window.innerWidth - 290)),
      y: rect.bottom + 4,
    });
  };

  // ── column widths ──
  const startResize = (colId: string, e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthOf(colId);
    let w = startW;
    const move = (ev: MouseEvent) => {
      w = Math.max(48, Math.round(startW + ev.clientX - startX));
      setWidths((ws) => ({ ...ws, [colId]: w }));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      savePref(WIDTHS_KEY + category, { ...loadPref(WIDTHS_KEY + category, {}), [colId]: w });
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
  const resetWidth = (colId: string) => {
    const next = { ...widths };
    delete next[colId];
    setWidths(next);
    savePref(WIDTHS_KEY + category, next);
  };

  // ── selection ──
  const select = (r: number, c: number, extend = false) => {
    if (!rows.length || !shown.length) return;
    const row = rows[Math.max(0, Math.min(rows.length - 1, r))].id;
    const col = shown[Math.max(0, Math.min(shown.length - 1, c))].id;
    setSel((s) =>
      extend && s ? { ...s, endRow: row, endCol: col } : { row, col, endRow: row, endCol: col },
    );
  };

  // ── writing ──
  const write = (row: SheetRow, col: Col, v: unknown) => {
    const key = pendingKey(row, col);
    const settle = (shownValue: unknown) => () =>
      setPending((p) => {
        if (!(key in p) || p[key] !== shownValue) return p;
        const next = { ...p };
        delete next[key];
        return next;
      });
    if (col.cell.kind === 'own') {
      const text = typeof v === 'string' ? v : '';
      if ((ctx.own(row.record.id)[col.cell.name] ?? '') === text) return;
      setPending((p) => ({ ...p, [key]: text }));
      void saveSheetValue(row.record.id, col.cell.name, text).finally(settle(text));
      return;
    }
    if (same(cellValue(row, col.cell, ctx), v)) return;
    setPending((p) => ({ ...p, [key]: v }));
    const cell = col.cell;
    void queueRecordEdit(row.record.id, (values) => withCellValue(values, row, cell, ctx, v)).finally(
      settle(v),
    );
  };

  const startEdit = (r: number, c: number, initial?: string) => {
    const row = rows[r];
    const col = shown[c];
    if (!row || !col || !cellEditable(row, col, perm, ctx)) return;
    if (col.editor === 'bool') return write(row, col, currentValue(row, col, ctx, pending) !== true);
    const picks = col.editor === 'enum' || col.editor === 'own';
    setEditing({ row: row.id, col: col.id, initial: picks ? undefined : initial });
  };

  const finishEdit: DoneFn = (row, col, v, move, refocus) => {
    setEditing(null);
    if (v !== undefined) write(row, col, v);
    if (refocus) gridRef.current?.focus({ preventScroll: true });
    const p = pos(row.id, col.id);
    if (p && move)
      select(
        p.r + (move === 'down' ? 1 : move === 'up' ? -1 : 0),
        p.c + (move === 'right' ? 1 : move === 'left' ? -1 : 0),
      );
  };
  // The editor calls back through a stable function, so rows can skip re-rendering.
  useLayoutEffect(() => {
    done.current = finishEdit;
  });
  const onDone = useCallback<DoneFn>((...args) => done.current?.(...args), []);

  // ── clipboard ──
  const copy = () => {
    if (!range) return;
    const grid: string[][] = [];
    for (let r = range.r0; r <= range.r1; r++) {
      const line: string[] = [];
      for (let c = range.c0; c <= range.c1; c++) line.push(displayText(rows[r], shown[c], ctx, pending));
      grid.push(line);
    }
    if (!navigator.clipboard) return toast(t('data.sheet.copyFailed'), 'error');
    navigator.clipboard.writeText(toTsv(grid)).then(
      () => toast(t('common.copied')),
      () => toast(t('data.sheet.copyFailed'), 'error'),
    );
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (editing || !at) return;
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    // Plain text with line breaks pasted into a long-text cell is one value; cells from a spreadsheet
    // (or tab-separated text) spread over the cells from the active one.
    const fromSheet = /<table/i.test(e.clipboardData.getData('text/html'));
    const single = !fromSheet && !text.includes('\t') && shown[at.c].editor === 'multiline';
    const grid = single ? [[text]] : parseTsv(text);
    const edits: { row: SheetRow; col: Col; v: unknown }[] = [];
    let cells = 0;
    for (let i = 0; i < grid.length && at.r + i < rows.length; i++)
      for (let j = 0; j < grid[i].length && at.c + j < shown.length; j++) {
        cells++;
        const row = rows[at.r + i];
        const col = shown[at.c + j];
        if (!col.editor || !cellEditable(row, col, perm, ctx)) continue;
        const v = parseInput(grid[i][j], col.editor, { int: col.int, choices: col.choices });
        if (v === undefined)
          return toast(t('data.sheet.badValue', { text: grid[i][j], column: col.label }), 'error');
        edits.push({ row, col, v });
      }
    if (cells > MAX_PASTE) return toast(t('data.sheet.tooMany', { max: MAX_PASTE }), 'error');
    if (!edits.length) return toast(t('data.sheet.readOnly'), 'error');
    for (const ed of edits) write(ed.row, ed.col, ed.v);
    const last = edits[edits.length - 1];
    setSel({ row: rows[at.r].id, col: shown[at.c].id, endRow: last.row.id, endCol: last.col.id });
  };

  // ── keyboard ──
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (editing || e.target !== gridRef.current) return;
    const mod = e.ctrlKey || e.metaKey;
    const nav = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'];
    if (!at || !end) {
      if (!nav.includes(e.key)) return;
      select(0, 0);
      return e.preventDefault();
    }
    const from = e.shiftKey ? end : at;
    const far = mod ? rows.length + shown.length : 1;
    switch (e.key) {
      case 'ArrowUp':
        select(from.r - far, from.c, e.shiftKey);
        break;
      case 'ArrowDown':
        select(from.r + far, from.c, e.shiftKey);
        break;
      case 'ArrowLeft':
        select(from.r, from.c - far, e.shiftKey);
        break;
      case 'ArrowRight':
        select(from.r, from.c + far, e.shiftKey);
        break;
      case 'PageUp':
        select(from.r - 15, from.c, e.shiftKey);
        break;
      case 'PageDown':
        select(from.r + 15, from.c, e.shiftKey);
        break;
      case 'Home':
        select(mod ? 0 : from.r, 0, e.shiftKey);
        break;
      case 'End':
        select(mod ? rows.length - 1 : from.r, shown.length - 1, e.shiftKey);
        break;
      case 'Tab':
        select(at.r, at.c + (e.shiftKey ? -1 : 1));
        break;
      case 'Enter':
      case 'F2':
        startEdit(at.r, at.c);
        break;
      case 'Escape':
        select(at.r, at.c);
        break;
      case 'Delete':
      case 'Backspace': {
        // Only the active cell, and only text: a cleared range is too easy to do by mistake.
        const row = rows[at.r];
        const col = shown[at.c];
        const text = col.editor === 'text' || col.editor === 'multiline' || col.editor === 'own';
        if (text && cellEditable(row, col, perm, ctx)) write(row, col, '');
        break;
      }
      default:
        if (mod && e.key.toLowerCase() === 'c') copy();
        else if (mod && e.key.toLowerCase() === 'a') {
          if (rows.length && shown.length)
            setSel({
              row: rows[0].id,
              col: shown[0].id,
              endRow: rows[rows.length - 1].id,
              endCol: shown[shown.length - 1].id,
            });
        } else if (!mod && !e.altKey && e.key.length === 1) startEdit(at.r, at.c, e.key);
        else return;
    }
    e.preventDefault();
  };

  // ── mouse (delegated: one handler for all cells) ──
  const cellOf = (target: EventTarget) => {
    const td = (target as HTMLElement).closest<HTMLElement>('td[data-row]');
    return td ? pos(td.dataset.row ?? '', td.dataset.col ?? '') : null;
  };
  const onCellMouseDown = (e: ReactMouseEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('.cell-editor')) return;
    const p = cellOf(e.target);
    if (!p) return;
    e.preventDefault(); // no text selection while dragging; the grid keeps the keyboard focus
    gridRef.current?.focus({ preventScroll: true });
    select(p.r, p.c, e.shiftKey && !!sel);
    dragging.current = true;
  };
  const onCellMouseOver = (e: ReactMouseEvent) => {
    if (!dragging.current) return;
    const p = cellOf(e.target);
    if (p) select(p.r, p.c, true);
  };
  const onCellClick = (e: ReactMouseEvent) => {
    const action = (e.target as HTMLElement).closest<HTMLElement>('[data-action]')?.dataset.action;
    const p = cellOf(e.target);
    if (!action || !p) return;
    const row = rows[p.r];
    const col = shown[p.c];
    if (action === 'open') setData({ selectedId: row.record.id });
    else if (action === 'revert') {
      const change = cellChange(row, col.cell, ctx);
      if (change?.inUnity && cellEditable(row, col, perm, ctx)) write(row, col, change.unity);
    } else if (action === 'toggle') startEdit(p.r, p.c);
  };
  const onCellDoubleClick = (e: ReactMouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-action], .cell-editor')) return;
    const p = cellOf(e.target);
    if (p) startEdit(p.r, p.c);
  };

  const menuCol = menu ? allCols.find((c) => c.id === menu.col) : undefined;
  const menuOptions = useMemo(
    () =>
      menuCol && !menuCol.long
        ? valueOptions(filterRows(allRows, allCols, view, ctx, menuCol.id), menuCol, ctx)
        : [],
    [menuCol, allRows, allCols, view, ctx],
  );
  const arrow = (id: string) => (view.sort.col === id ? (view.sort.dir === 1 ? ' ▲' : ' ▼') : '');
  const totalWidth = shown.reduce((sum, c) => sum + widthOf(c.id), 0);

  return (
    <div className="table-view">
      <header className="table-head">
        <h2>{t(`data.categories.${category}`, { defaultValue: cat?.label ?? category })}</h2>
        <span className="muted small">
          {t('data.sheet.count', { records: recordCount, rows: rows.length })}
        </span>
        {filterDefs && <CategoryFilters category={category} filters={filterDefs} inline />}
        <div className="spacer" />
        <input
          className="table-filter"
          value={view.search}
          placeholder={t('data.sheet.search')}
          onChange={(e) => setView({ search: e.target.value })}
        />
        <label className="check-row small" title={t('data.sheet.onlyChangedHint')}>
          <input
            type="checkbox"
            checked={view.onlyChanged}
            onChange={(e) => setView({ onlyChanged: e.target.checked })}
          />
          {t('data.sheet.onlyChanged')}
        </label>
        <label className="check-row small" title={t('data.sheet.fullTextHint')}>
          <input
            type="checkbox"
            checked={fullText}
            onChange={(e) => {
              setFullText(e.target.checked);
              savePref(FULL_TEXT_KEY, e.target.checked);
            }}
          />
          {t('data.sheet.fullText')}
        </label>
        {anyFilter && (
          <button className="link small" onClick={clearAll}>
            {t('data.filters.clear')}
          </button>
        )}
        <details className="col-picker">
          <summary className="small">
            {t('data.table.columns', { shown: shown.length, all: allCols.length })}
          </summary>
          <div className="col-picker-list">
            {allCols
              .filter((c) => c.id !== 'name')
              .map((c) => (
                <label key={c.id} className="check-row small">
                  <input
                    type="checkbox"
                    checked={!hidden.includes(c.id)}
                    onChange={() => toggleColumn(c.id)}
                  />
                  {c.label}
                </label>
              ))}
          </div>
        </details>
        {perm.values && cat?.creatable && !cat.prefab && (
          <button className="small" onClick={() => setCreating(true)}>
            + {t('data.create.button')}
          </button>
        )}
        <ViewSwitch category={category} />
      </header>
      <p className="muted small">
        {perm.editor ? t('data.sheet.help') : t('data.sheet.helpRead')}
        {allCols.some((c) => c.cell.kind === 'own') && ` ${t('data.sheet.helpOwn')}`}
      </p>
      {perm.editor && !perm.values && (
        <div className="banner warn">
          {t('data.sheet.readOnlyCategory')}
          {allCols.some((c) => c.cell.kind === 'own') && ` ${t('data.sheet.ownStillEditable')}`}
        </div>
      )}
      {creating && <NewRecordDialog category={category} onClose={() => setCreating(false)} />}
      <div
        ref={gridRef}
        className="table-scroll sheet-grid"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        style={{ scrollPaddingLeft: colIdx.has('name') ? widthOf('name') : 0 }}
      >
        <table className={`data-table sheet${fullText ? '' : ' clamp'}`} style={{ width: totalWidth }}>
          <colgroup>
            {shown.map((c) => (
              <col key={c.id} style={{ width: widthOf(c.id) }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {shown.map((c) => (
                <th
                  key={c.id}
                  className={[c.id === 'name' ? 'sticky-col' : '', c.cell.kind === 'own' ? 'own' : '']
                    .filter(Boolean)
                    .join(' ')}
                  title={
                    c.tooltip
                      ? `${c.label}
${c.tooltip}`
                      : c.label
                  }
                >
                  <div className="sheet-th">
                    <button className="sheet-th-label" tabIndex={-1} onClick={() => sortBy(c.id)}>
                      {c.label}
                      {arrow(c.id)}
                    </button>
                    <button
                      className={`sheet-filter-btn${isFilterActive(view.filters[c.id]) ? ' active' : ''}`}
                      tabIndex={-1}
                      aria-label={t('data.sheet.filterColumn', { column: c.label })}
                      title={t('data.sheet.filterColumn', { column: c.label })}
                      onClick={(e) => toggleMenu(c.id, e)}
                    >
                      ▾
                    </button>
                  </div>
                  <span
                    className="col-resize"
                    onMouseDown={(e) => startResize(c.id, e)}
                    onDoubleClick={() => resetWidth(c.id)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            onMouseDown={onCellMouseDown}
            onMouseOver={onCellMouseOver}
            onClick={onCellClick}
            onDoubleClick={onCellDoubleClick}
          >
            {rows.map((row, i) => {
              const inRange = multi && !!range && i >= range.r0 && i <= range.r1;
              return (
                <SheetRowView
                  key={row.id}
                  row={row}
                  cols={shown}
                  ctx={ctx}
                  perm={perm}
                  groupStart={i === 0 || rows[i - 1].record.id !== row.record.id}
                  activeCol={at && at.r === i ? at.c : -1}
                  rangeFrom={inRange && range ? range.c0 : -1}
                  rangeTo={inRange && range ? range.c1 : -1}
                  editCol={editing?.row === row.id ? (colIdx.get(editing.col) ?? -1) : -1}
                  editInitial={editing?.row === row.id ? editing.initial : undefined}
                  pending={pending}
                  onDone={onDone}
                />
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <p className="muted sheet-empty">{t('data.sheet.noRows')}</p>}
      </div>
      {menu && menuCol && (
        <FilterMenu
          menu={menu}
          col={menuCol}
          options={menuOptions}
          filter={view.filters[menuCol.id]}
          onFilter={(f) => setColumnFilter(menuCol.id, f)}
          onSort={(dir) => sortBy(menuCol.id, dir)}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

interface RowProps {
  row: SheetRow;
  cols: Col[];
  ctx: SheetCtx;
  perm: Perm;
  /** first row of its record: a heavier line above, and record-level cells shown at full strength */
  groupStart: boolean;
  activeCol: number;
  rangeFrom: number;
  rangeTo: number;
  editCol: number;
  editInitial?: string;
  pending: Record<string, unknown>;
  onDone: DoneFn;
}

const SheetRowView = memo(function SheetRowView({
  row,
  cols,
  ctx,
  perm,
  groupStart,
  activeCol,
  rangeFrom,
  rangeTo,
  editCol,
  editInitial,
  pending,
  onDone,
}: RowProps) {
  return (
    <tr className={groupStart ? 'group-start' : undefined}>
      {cols.map((col, ci) => (
        <SheetCellView
          key={col.id}
          row={row}
          col={col}
          ctx={ctx}
          perm={perm}
          repeat={!groupStart && !perEntry(col.cell)}
          active={ci === activeCol}
          inRange={ci >= rangeFrom && ci <= rangeTo}
          editing={ci === editCol}
          editInitial={editInitial}
          pending={pending}
          onDone={onDone}
        />
      ))}
    </tr>
  );
});

function SheetCellView({
  row,
  col,
  ctx,
  perm,
  repeat,
  active,
  inRange,
  editing,
  editInitial,
  pending,
  onDone,
}: {
  row: SheetRow;
  col: Col;
  ctx: SheetCtx;
  perm: Perm;
  repeat: boolean;
  active: boolean;
  inRange: boolean;
  editing: boolean;
  editInitial?: string;
  pending: Record<string, unknown>;
  onDone: DoneFn;
}) {
  const { t } = useTranslation();
  const editable = cellEditable(row, col, perm, ctx);
  const change = cellChange(row, col.cell, ctx);
  const saving = pendingKey(row, col) in pending;
  const na = row.index < 0 && perEntry(col.cell);
  const cls = [
    col.id === 'name' && 'sticky-col',
    col.numeric && 'num',
    change && 'changed',
    active && 'active',
    inRange && 'in-range',
    repeat && 'repeat',
    na && 'na',
    editing && 'editing',
    saving && 'saving',
  ]
    .filter(Boolean)
    .join(' ');
  const title = change
    ? change.inUnity
      ? `${t('data.edit.unityHas')} ${valueText(change.unity, col.field, ctx) || '—'}`
      : t('data.sheet.notInUnity')
    : undefined;

  let content: ReactNode;
  if (editing) {
    content = (
      <CellEditor
        row={row}
        col={col}
        value={currentValue(row, col, ctx, pending)}
        initial={editInitial}
        onDone={onDone}
      />
    );
  } else if (col.cell.kind === 'status') {
    const missing = row.record.syncState === 'missing_in_unity';
    content = (
      <span className={`status-pill ${missing ? 'missing_in_unity' : row.record.status}`}>
        {statusText(row.record, ctx)}
      </span>
    );
  } else if (col.field?.kind === 'bool' && !na) {
    const on = currentValue(row, col, ctx, pending) === true;
    content = (
      <span
        className={`sheet-check${on ? ' on' : ''}${editable ? '' : ' ro'}`}
        role="checkbox"
        aria-checked={on}
        data-action={editable ? 'toggle' : undefined}
      >
        {on ? '✓' : ''}
      </span>
    );
  } else {
    const text = displayText(row, col, ctx, pending);
    const body = text ? (
      <span className="cell-text">{text}</span>
    ) : col.empty && !na ? (
      <span className="cell-text muted">{t(col.empty)}</span>
    ) : null;
    content =
      col.id === 'name' ? (
        <div className="sheet-name">
          <DataIcon iconKey={row.record.icon} size={16} fallback={categoryIcon(row.record.category)} />
          {body}
          {!row.record.guid && <span className="chip">{t('data.create.newBadge')}</span>}
          <button className="sheet-open" data-action="open" tabIndex={-1} title={t('data.sheet.open')}>
            ↗
          </button>
        </div>
      ) : (
        body
      );
  }
  return (
    <td data-row={row.id} data-col={col.id} className={cls || undefined} title={title}>
      {content}
      {change?.inUnity && editable && !editing && !saving && (
        <button className="cell-revert" data-action="revert" tabIndex={-1} title={t('data.sheet.revert')}>
          ↺
        </button>
      )}
    </td>
  );
}

/** In-place editor: text, long text (grows), number, or a pick list for enums and our option columns. */
function CellEditor({
  row,
  col,
  value,
  initial,
  onDone,
}: {
  row: SheetRow;
  col: Col;
  value: unknown;
  initial?: string;
  onDone: DoneFn;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const finished = useRef(false);
  const box = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const kind = col.editor ?? 'text';
  const finish = (v: unknown, move: Move, refocus = true) => {
    if (finished.current) return;
    finished.current = true;
    onDone(row, col, v, move, refocus);
  };

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange?.(el.value.length, el.value.length);
    if (el.tagName === 'TEXTAREA') {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, []);

  if (kind === 'enum' || kind === 'own')
    return (
      <ChoiceList
        choices={col.choices ?? []}
        value={typeof value === 'string' ? value : ''}
        allowEmpty={kind === 'own'}
        onPick={(v, move) => finish(v, move)}
        onCancel={(move, refocus) => finish(undefined, move, refocus)}
      />
    );

  const start =
    initial ??
    (kind === 'number'
      ? typeof value === 'number'
        ? formatNumber(value)
        : ''
      : typeof value === 'string'
        ? value
        : '');
  const commit = (raw: string, move: Move, refocus = true) => {
    const v = parseInput(raw, kind, { int: col.int });
    if (v !== undefined) return finish(v, move, refocus);
    toast(t('data.sheet.badValue', { text: raw, column: col.label }), 'error');
    if (!refocus) finish(undefined, null, false); // focus went elsewhere: give up the edit
  };
  const grow = (el: HTMLInputElement | HTMLTextAreaElement) => {
    if (el.tagName !== 'TEXTAREA') return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.stopPropagation();
    const el = e.currentTarget;
    if (e.key === 'Escape') {
      e.preventDefault();
      finish(undefined, null);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commit(el.value, e.shiftKey ? 'left' : 'right');
    } else if (e.key === 'Enter' && kind === 'multiline' && e.altKey) {
      // Alt+Enter is Excel's line break; Shift+Enter works on its own in a textarea.
      e.preventDefault();
      el.setRangeText('\n', el.selectionStart ?? el.value.length, el.selectionEnd ?? el.value.length, 'end');
      grow(el);
    } else if (e.key === 'Enter' && !(kind === 'multiline' && e.shiftKey)) {
      e.preventDefault();
      commit(el.value, e.shiftKey ? 'up' : 'down');
    }
  };

  if (kind === 'multiline')
    return (
      <textarea
        ref={box}
        className="cell-editor"
        defaultValue={start}
        rows={1}
        onKeyDown={onKey}
        onInput={(e) => grow(e.currentTarget)}
        onBlur={(e) => commit(e.currentTarget.value, null, false)}
      />
    );
  return (
    <>
      <input
        ref={box}
        className="cell-editor"
        defaultValue={start}
        inputMode={kind === 'number' ? 'decimal' : undefined}
        list={col.vocab?.length ? listId : undefined}
        onKeyDown={onKey}
        onBlur={(e) => commit(e.currentTarget.value, null, false)}
      />
      {!!col.vocab?.length && (
        <datalist id={listId}>
          {col.vocab.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      )}
    </>
  );
}

/** A pick list under the cell: arrows + Enter, or a click. Unlike a <select>, arrows don't save. */
function ChoiceList({
  choices,
  value,
  allowEmpty,
  onPick,
  onCancel,
}: {
  choices: Choice[];
  value: string;
  allowEmpty: boolean;
  onPick: (v: string, move: Move) => void;
  onCancel: (move: Move, refocus: boolean) => void;
}) {
  const items = useMemo(() => {
    const list = allowEmpty ? [{ value: '', label: '—' }, ...choices] : choices;
    return value && !list.some((c) => c.value === value) ? [{ value, label: value }, ...list] : list;
  }, [choices, value, allowEmpty]);
  const [hi, setHi] = useState(() =>
    Math.max(
      0,
      items.findIndex((c) => c.value === value),
    ),
  );
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => box.current?.focus({ preventScroll: true }), []);
  useEffect(() => {
    box.current?.children[hi]?.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  return (
    <div
      ref={box}
      className="cell-editor cell-choices"
      tabIndex={0}
      role="listbox"
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'ArrowDown') setHi((h) => Math.min(items.length - 1, h + 1));
        else if (e.key === 'ArrowUp') setHi((h) => Math.max(0, h - 1));
        else if (e.key === 'Enter' && items[hi]) onPick(items[hi].value, null);
        else if (e.key === 'Tab' && items[hi]) onPick(items[hi].value, e.shiftKey ? 'left' : 'right');
        else if (e.key === 'Escape') onCancel(null, true);
        else return;
        e.preventDefault();
      }}
      onBlur={() => onCancel(null, false)}
    >
      {items.map((c, i) => (
        <div
          key={c.value}
          role="option"
          aria-selected={c.value === value}
          className={`cell-choice${i === hi ? ' hi' : ''}${c.value === value ? ' current' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(c.value, null);
          }}
        >
          {c.label}
        </div>
      ))}
    </div>
  );
}

/** A column's menu: sort, and filter by its values (or by text for long-text columns). */
function FilterMenu({
  menu,
  col,
  options,
  filter,
  onFilter,
  onSort,
  onClose,
}: {
  menu: Menu;
  col: Col;
  options: ValueOption[];
  filter: ColumnFilter | undefined;
  onFilter: (f: ColumnFilter | undefined) => void;
  onSort: (dir: 1 | -1) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [find, setFind] = useState('');

  useEffect(() => {
    const down = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!box.current?.contains(target) && !target.closest?.('.sheet-filter-btn')) onClose();
    };
    const key = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', down);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('mousedown', down);
      window.removeEventListener('keydown', key);
    };
  }, [onClose]);

  const emptyLabel = col.empty ? t(col.empty) : t('data.filters.unset');
  const all = options.map((o) => o.value);
  const chosen = filter && 'values' in filter ? filter.values : null; // null = everything
  const needle = find.trim().toLowerCase();
  const listed = needle
    ? options.filter((o) => (o.value || emptyLabel).toLowerCase().includes(needle))
    : options;
  const toggle = (v: string) => {
    const current = chosen ?? all;
    const next = current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
    onFilter(all.every((x) => next.includes(x)) ? undefined : { values: next });
  };

  return (
    <div ref={box} className="sheet-menu" style={{ left: menu.x, top: menu.y }}>
      <div className="row">
        <button className="small" onClick={() => onSort(1)}>
          {t('data.sheet.sortAsc')}
        </button>
        <button className="small" onClick={() => onSort(-1)}>
          {t('data.sheet.sortDesc')}
        </button>
      </div>
      {col.long ? (
        <input
          autoFocus
          placeholder={t('data.sheet.contains')}
          value={filter && 'text' in filter ? filter.text : ''}
          onChange={(e) => onFilter(e.target.value ? { text: e.target.value } : undefined)}
        />
      ) : (
        <>
          {options.length > 8 && (
            <input
              autoFocus
              placeholder={t('data.sheet.findValue')}
              value={find}
              onChange={(e) => setFind(e.target.value)}
            />
          )}
          <div className="row small">
            <button
              className="link small"
              onClick={() => onFilter(needle ? { values: listed.map((o) => o.value) } : undefined)}
            >
              {t('data.sheet.selectAll')}
            </button>
            <button className="link small" onClick={() => onFilter({ values: [] })}>
              {t('data.sheet.selectNone')}
            </button>
          </div>
          <div className="sheet-menu-list">
            {listed.map((o) => (
              <label key={o.value} className="check-row small">
                <input
                  type="checkbox"
                  checked={!chosen || chosen.includes(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <span className={o.value ? 'ellipsis' : 'ellipsis muted'}>{o.value || emptyLabel}</span>
                <span className="count muted">{o.count}</span>
              </label>
            ))}
          </div>
        </>
      )}
      <div className="row">
        <button className="small" disabled={!isFilterActive(filter)} onClick={() => onFilter(undefined)}>
          {t('data.sheet.clearFilter')}
        </button>
        <div className="spacer" />
        <button className="small" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}
